import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';

const VIMEO_EMBED_URL =
  'https://player.vimeo.com/video/1067784546?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1';

// Checklist phases: each item goes pending → inprogress → completed sequentially
// phase 0: [inprogress, pending, pending]
// phase 1: [almostdone, pending, pending]
// phase 2: [completed, inprogress, pending]
// phase 3: [completed, almostdone, pending]
// phase 4: [completed, completed, inprogress]
// phase 5: [completed, completed, almostdone]
// phase 6: [completed, completed, completed] → loop
const PHASES = [
  ['inprogress', 'pending', 'pending'],
  ['almostdone', 'pending', 'pending'],
  ['completed', 'inprogress', 'pending'],
  ['completed', 'almostdone', 'pending'],
  ['completed', 'completed', 'inprogress'],
  ['completed', 'completed', 'almostdone'],
  ['completed', 'completed', 'completed'],
];

const CHECKLIST_ITEMS = [
  { id: 0, label: 'Creating User Account' },
  { id: 1, label: 'Loading Dashboard' },
  { id: 2, label: 'Adding Guru Session' },
];

const STEP_CARDS = [
  {
    step: 'Step 1:',
    title: 'Ask Smart Questions',
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect x="6" y="8" width="28" height="24" rx="3" fill="#c8d8f0" />
        <rect x="10" y="14" width="8" height="2" rx="1" fill="#2c7be5" />
        <rect x="10" y="18" width="20" height="2" rx="1" fill="#2c7be5" />
        <rect x="10" y="22" width="14" height="2" rx="1" fill="#2c7be5" />
        <circle cx="31" cy="28" r="7" fill="#2c7be5" />
        <text x="31" y="32" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">?</text>
      </svg>
    ),
  },
  {
    step: 'Step 2:',
    title: 'Get Expert Insights',
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="13" r="6" fill="#c8d8f0" />
        <path d="M8 34c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="#c8d8f0" />
        <circle cx="20" cy="13" r="4" fill="#2c7be5" />
        <path d="M11 32c0-4.971 4.029-9 9-9s9 4.029 9 9" fill="#2c7be5" />
      </svg>
    ),
  },
  {
    step: 'Step 3:',
    title: 'Review Your Results',
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect x="6" y="28" width="6" height="8" rx="1" fill="#c8d8f0" />
        <rect x="14" y="20" width="6" height="16" rx="1" fill="#2c7be5" />
        <rect x="22" y="14" width="6" height="22" rx="1" fill="#2c7be5" opacity="0.7" />
        <rect x="30" y="8" width="6" height="28" rx="1" fill="#2c7be5" opacity="0.5" />
      </svg>
    ),
  },
];

// Spinner icon for "Almost Done"
const SpinnerIcon = ({ color = '#fd7e14' }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    style={{ animation: 'spin 1.2s linear infinite' }}
  >
    <circle cx="10" cy="10" r="8" fill="none" stroke="#e9ecef" strokeWidth="3" />
    <path
      d="M10 2 A8 8 0 0 1 18 10"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="9" fill="#28a745" />
    <polyline points="5,10 8,14 15,7" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PendingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="9" fill="none" stroke="#ced4da" strokeWidth="2.5" />
    <circle cx="10" cy="10" r="4" fill="#ced4da" />
  </svg>
);

const statusLabel = status => {
  if (status === 'completed') return 'Completed';
  if (status === 'almostdone') return 'Almost Done...';
  if (status === 'inprogress') return 'In Progress...';
  return 'Pending...';
};

const statusColor = status => {
  if (status === 'completed') return '#28a745';
  if (status === 'almostdone') return '#fd7e14';
  if (status === 'inprogress') return '#2c7be5';
  return '#adb5bd';
};

const StatusIcon = ({ status }) => {
  if (status === 'completed') return <CheckIcon />;
  if (status === 'almostdone') return <SpinnerIcon color="#fd7e14" />;
  if (status === 'inprogress') return <SpinnerIcon color="#2c7be5" />;
  return <PendingIcon />;
};

const VideoThumbnail = ({ onPlay }) => (
  <div
    style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      borderRadius: '10px',
      overflow: 'hidden',
      backgroundImage: 'url(/img/intro-thumbnail.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      cursor: 'pointer',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    }}
    onClick={onPlay}
  >
    {/* Dark overlay for readability */}
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
    {/* Play button overlay */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          border: '2px solid rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
          <polygon points="4,2 18,10 4,18" />
        </svg>
      </div>
    </div>
    {/* Video controls bar */}
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(0,0,0,0.6)',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><polygon points="1,1 6,7 1,13" /></svg>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><rect x="1" y="1" width="4" height="12" /><rect x="9" y="1" width="4" height="12" /></svg>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
        <polygon points="0,4 4,4 4,0" opacity="0.7"/><line x1="2" y1="2" x2="6" y2="6" stroke="white" strokeWidth="1.5"/>
      </svg>
      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>01:23 / 2:45</span>
      <div style={{ flex: 1, height: '3px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px', marginLeft: '4px' }}>
        <div style={{ width: '35%', height: '100%', background: 'white', borderRadius: '2px' }} />
      </div>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><path d="M1 5 Q3 1 7 3 Q11 1 13 5" strokeWidth="1.5" stroke="white" fill="none"/></svg>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><rect x="1" y="3" width="8" height="8" rx="1"/><polygon points="9,5 13,2 13,12 9,9"/></svg>
    </div>
  </div>
);

const ProgressBar = ({ phase }) => {
  const progress = Math.min(100, Math.round((phase / (PHASES.length - 1)) * 100));
  return (
    <div>
      <div style={{ height: '6px', background: '#c8d8f0', borderRadius: '3px', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #2c7be5, #1a5fa8)',
            borderRadius: '3px',
            transition: 'width 0.6s ease',
          }}
        />
      </div>
    </div>
  );
};

const IntroPage = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [phase, setPhase] = useState(0);
  const history = useHistory();

  useEffect(() => {
    if (phase >= PHASES.length - 1) return;
    const timer = setTimeout(() => setPhase(p => p + 1), 2000);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const statuses = PHASES[phase];

  const content = (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .intro-step-card:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.12) !important; }

        /* ── Mobile portrait (≤640px) ── */
        @media (max-width: 640px) {
          .intro-container {
            padding: 12px !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }
          .intro-header h2 { font-size: 20px !important; }
          .intro-header p  { font-size: 13px !important; margin-top: 4px !important; }
          .intro-header-dot { margin-top: 8px !important; }
          .intro-main {
            flex-direction: column !important;
            gap: 12px !important;
            overflow-y: visible !important;
          }
          .intro-left  { flex: none !important; gap: 8px !important; }
          .intro-video { flex: none !important; height: 180px !important; min-height: 0 !important; }
          .intro-right {
            flex: none !important;
            align-self: stretch !important;
            padding: 14px 12px !important;
          }
          .intro-right h5 { font-size: 14px !important; margin-bottom: 10px !important; }
          .intro-step-card { padding: 8px 6px !important; }
          .intro-step-card svg { width: 28px !important; height: 28px !important; }
          .intro-buttons { gap: 10px !important; }
          .intro-buttons button { min-width: 0 !important; flex: 1 !important; font-size: 14px !important; padding: 9px 12px !important; }
        }

        /* ── Mobile landscape (height ≤ 480px) ── */
        @media (max-height: 480px) {
          .intro-container { padding: 8px 16px !important; }
          .intro-header h2 { font-size: 18px !important; }
          .intro-header p  { display: none !important; }
          .intro-header-dot { display: none !important; }
        }
      `}</style>

      {/* Full-screen overlay covering navbar and sidebar */}
      <div
        className="intro-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          background: 'linear-gradient(160deg, #3a8fd4 0%, #2470b8 35%, #1a5598 65%, #1a4a8a 100%)',
          padding: '16px 24px 16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Decorative wave shapes */}
        <div style={{
          position: 'absolute', bottom: '-40px', left: '-60px', width: '500px', height: '300px',
          borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-80px', right: '-80px', width: '600px', height: '400px',
          borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '30%', right: '5%', width: '200px', height: '200px',
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none',
        }} />

        {/* Header */}
        <div className="intro-header" style={{ textAlign: 'center', marginBottom: '12px', position: 'relative', flexShrink: 0 }}>
          <h2 style={{ color: '#fff', fontWeight: '800', fontSize: '28px', margin: 0, textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            Setting Things Up...
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', marginTop: '8px', marginBottom: 0 }}>
            Please hold on a moment while we get everything ready for you.
          </p>
          {/* Connector dot */}
          <div className="intro-header-dot" style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255,255,255,0.6)' }} />
          </div>
        </div>

        {/* Main content */}
        <div className="intro-main" style={{ flex: 1, minHeight: 0, display: 'flex', gap: '24px', alignItems: 'stretch', maxWidth: 'min(1300px, calc(100vw - 48px))', margin: '0 auto', width: '100%', position: 'relative' }}>
          {/* Left: Video + Steps + Progress */}
          <div className="intro-left" style={{ flex: 3, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Video — fills available flex space */}
            <div className="intro-video" style={{ flex: 1, minHeight: 0, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', position: 'relative' }}>
              {videoPlaying ? (
                <iframe
                  src={VIMEO_EMBED_URL}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                  allow="autoplay; fullscreen; picture-in-picture"
                  title="eScoreIT Introduction"
                />
              ) : (
                <VideoThumbnail onPlay={() => setVideoPlaying(true)} />
              )}
            </div>

            {/* Step Cards */}
            <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
              {STEP_CARDS.map(card => (
                <div
                  key={card.step}
                  className="intro-step-card"
                  style={{
                    flex: 1,
                    background: 'rgba(255,255,255,0.92)',
                    borderRadius: '8px',
                    padding: '12px 10px',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>{card.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#2c7be5', marginBottom: '2px' }}>{card.step}</div>
                  <div style={{ fontSize: '12px', color: '#3d4555', fontWeight: '600' }}>{card.title}</div>
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <ProgressBar phase={phase} />

            {/* Buttons — centered under the video */}
            <div className="intro-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexShrink: 0 }}>
              <Button
                onClick={() => setVideoPlaying(true)}
                disabled={videoPlaying}
                style={{
                  minWidth: '160px',
                  fontWeight: '700',
                  fontSize: '15px',
                  background: videoPlaying ? '#1a5598' : 'linear-gradient(135deg, #2c7be5, #1a5598)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 28px',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                  color: '#fff',
                }}
              >
                Start Session
              </Button>
              <Button
                onClick={() => history.push('/')}
                style={{
                  minWidth: '140px',
                  fontWeight: '700',
                  fontSize: '15px',
                  background: 'rgba(255,255,255,0.15)',
                  border: '2px solid rgba(255,255,255,0.5)',
                  borderRadius: '6px',
                  padding: '10px 28px',
                  color: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                Skip Intro
              </Button>
            </div>
          </div>

          {/* Right: Progress Checklist */}
          <div
            className="intro-right"
            style={{
              flex: 2,
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '10px',
              padding: '20px 18px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              alignSelf: 'flex-start',
            }}
          >
            <h5 style={{ fontWeight: '700', color: '#1a2a4a', marginBottom: '18px', fontSize: '16px' }}>
              Progress Checklist
            </h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {CHECKLIST_ITEMS.map((item, index) => (
                <div key={item.id}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', gap: '12px' }}>
                    <StatusIcon status={statuses[index]} />
                    <span
                      style={{
                        flex: 1,
                        fontWeight: '600',
                        fontSize: '14px',
                        color: statuses[index] === 'pending' ? '#adb5bd' : '#2d3748',
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        color: statusColor(statuses[index]),
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {statusLabel(statuses[index])}
                    </span>
                  </div>
                  {index < CHECKLIST_ITEMS.length - 1 && (
                    <div style={{ height: '1px', background: '#f0f2f5' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default IntroPage;
